import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { authSelectorsV1 } from '@/stores/authStore_v1';
import { useMyUsage } from '@/hooks/org-admin/useOrgUsage';
import { usageLimitSeverity, type UsageLimitSeverity } from '@/services/org-admin/usageThresholds';
import { formatCurrencyUsd } from '@/components/organisms/org-admin/usageFormat';
import { formatCycleEnd } from '@/components/molecules/org-admin/BillingMemberRow';
import { seatPlanLabel, type MemberUsage, type OrgBillingMember } from '@/services/org-admin';

/**
 * The navbar strip shown when the signed-in member's subscription is ending or has ended, or when
 * they are near or at their own AI usage limit.
 *
 * Purely presentational — `UsageLimitBannerHost` below owns fetching the usage figure and wording
 * the copy (which differs for an admin, who can raise the budget, versus a member, who can't).
 */
export interface UsageLimitBannerProps {
  severity: 'warning' | 'reached';
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  /** Omitted for a notice that must stay until it is resolved. */
  onDismiss?: () => void;
  className?: string;
}

export function UsageLimitBanner({
  severity,
  title,
  description,
  actionLabel,
  onAction,
  onDismiss,
  className,
}: UsageLimitBannerProps) {
  const isReached = severity === 'reached';

  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b px-3 py-2 sm:px-4',
        isReached ? 'border-destructive/28 bg-destructive/[0.08]' : 'border-warning/30 bg-warning/[0.08]',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <AlertTriangle
          className={cn('h-4 w-4 shrink-0', isReached ? 'text-destructive-strong' : 'text-warning-strong')}
          aria-hidden
        />
        <p className="text-[13px] leading-snug">
          <span className={cn('font-semibold', isReached ? 'text-destructive-strong' : 'text-warning-strong')}>
            {title}
          </span>{' '}
          <span className="text-foreground/85">{description}</span>
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {actionLabel && onAction && (
          <button
            type="button"
            onClick={onAction}
            className={cn(
              'rounded-md px-2.5 py-1 text-[12px] font-semibold text-white',
              isReached ? 'bg-destructive-strong hover:bg-destructive-strong/90' : 'bg-warning-strong hover:bg-warning-strong/90',
            )}
          >
            {actionLabel}
          </button>
        )}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className={cn(
              'rounded-md p-1',
              isReached ? 'text-destructive-strong hover:bg-destructive/10' : 'text-warning-strong hover:bg-warning/10',
            )}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

interface Notice {
  /** Which notice this is — a dismissal silences only this one, not whatever replaces it. */
  key: 'ended' | 'ending' | UsageLimitSeverity;
  severity: 'warning' | 'reached';
  title: string;
  description: string;
  actionLabel: string;
  dismissible: boolean;
}

/**
 * The seat's subscription state, which outranks the usage limit: an ended plan leaves a $0 budget,
 * so "you've reached your limit" would name the symptom instead of the cause.
 *
 * Members can't buy plans, so their copy points at an admin; an admin gets a button instead.
 */
export function subscriptionNotice(
  billing: OrgBillingMember | null | undefined,
  canManage: boolean,
  /**
   * Credits the member can still spend, from the same `/usage/members/:id` payload (backend #327).
   * Credits survive a plan ending, so an ended plan with credit left is a warning about what comes
   * next, not a "you are cut off" — which would be false while they can still run things.
   * `null`/`undefined` for a backend without the field, which keeps the pre-#327 wording.
   */
  creditRemainingUsd?: number | null,
): Notice | null {
  if (!billing) return null;

  if (billing.plan_ended_at) {
    const date = formatCycleEnd(billing.plan_ended_at)?.title;
    const plan = billing.ended_plan ? seatPlanLabel(billing.ended_plan) : null;
    const planPhrase = `${plan ? `Your ${plan} plan` : 'Your plan'} ended${date ? ` on ${date}` : ''}`;
    if (typeof creditRemainingUsd === 'number' && creditRemainingUsd > 0) {
      return {
        key: 'ended',
        severity: 'warning',
        title: 'Your subscription has ended.',
        description:
          `${planPhrase}. You can still use ${formatCurrencyUsd(creditRemainingUsd)} of credit, which does not expire.` +
          (canManage ? '' : ' Ask your org admin to buy you a plan to keep going after that.'),
        actionLabel: 'Buy a plan',
        dismissible: true,
      };
    }
    return {
      key: 'ended',
      severity: 'reached',
      title: 'Your subscription has expired.',
      description:
        `${planPhrase} and you have no AI credits left.` +
        (canManage ? '' : ' Ask your org admin to buy you a plan.'),
      actionLabel: 'Buy a plan',
      // AI features stay off until a plan is bought, so there is nothing to put off.
      dismissible: false,
    };
  }

  if (billing.cancel_at_period_end && billing.plan_cycle_ends_at) {
    const date = formatCycleEnd(billing.plan_cycle_ends_at)?.title;
    const plan = seatPlanLabel(billing.plan);
    return {
      key: 'ending',
      severity: 'warning',
      title: `${plan ? `Your ${plan} plan` : 'Your plan'} ends${date ? ` on ${date}` : ' soon'}.`,
      description:
        'It was cancelled and won’t renew.' +
        (canManage ? '' : ' Ask your org admin to renew it to keep using AI features.'),
      actionLabel: 'Renew',
      dismissible: true,
    };
  }

  return null;
}

function usageNotice(usage: MemberUsage, canManage: boolean): Notice | null {
  const severity = usageLimitSeverity({
    spendUsd: usage.spend_usd,
    budgetUsd: usage.budget_usd,
    overBudget: usage.over_budget,
  });
  if (severity === 'ok') return null;

  const budget = formatCurrencyUsd(usage.budget_usd);
  const isReached = severity === 'reached';
  return {
    key: severity,
    severity: isReached ? 'reached' : 'warning',
    title: isReached
      ? "You've reached your AI usage limit for this cycle."
      : "You're nearing your AI usage limit for this cycle.",
    description: isReached
      ? `Your budget of ${budget} has been used — AI features are paused until ${canManage ? 'your budget is raised or the cycle resets.' : 'your org admin raises your budget, or the cycle resets.'}`
      : `Most of your ${budget} budget has been used.`,
    actionLabel: 'Manage plan',
    dismissible: true,
  };
}

/**
 * Fetches the member's own usage and decides whether the banner shows at all.
 *
 * This lives in the app shell (`StudioLayout`), not in `Header` — `Header` is not mounted by any
 * route, so the banner was implemented into dead code and never appeared for anyone.
 */
export function UsageLimitBannerHost() {
  const navigate = useNavigate();
  const role = authSelectorsV1.selectedOrg()?.role;
  const canManageBudget = role === 'OWNER' || role === 'ADMIN';
  const myUsage = useMyUsage();
  // Scoped to the notice, not "forever" — if a warning gets dismissed and usage later crosses into
  // "reached", or the plan then ends, the banner reappears rather than staying silenced.
  const [dismissed, setDismissed] = useState<Notice['key'] | null>(null);

  if (!myUsage.data) return null;
  const notice =
    subscriptionNotice(myUsage.data.billing, canManageBudget, myUsage.data.credit_remaining_usd ?? null) ??
    usageNotice(myUsage.data, canManageBudget);
  if (!notice || notice.key === dismissed) return null;

  return (
    <UsageLimitBanner
      severity={notice.severity}
      title={notice.title}
      description={notice.description}
      actionLabel={canManageBudget ? notice.actionLabel : undefined}
      onAction={canManageBudget ? () => navigate('/studio/org-admin?tab=billing') : undefined}
      onDismiss={notice.dismissible ? () => setDismissed(notice.key) : undefined}
    />
  );
}
